import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Plus, Trash2, Power } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PackagesTab({ adminToken }) {
  const getHeaders = () => ({ Authorization: `Bearer ${adminToken}` });
  const [packages, setPackages] = useState([]);
  const [newPkg, setNewPkg] = useState({ name: "", price: "", features: "" });
  const [creating, setCreating] = useState(false);

  const fetchPackages = useCallback(async () => {
    try { const res = await axios.get(`${API}/admin/packages`, { headers: { Authorization: `Bearer ${adminToken}` } }); setPackages(res.data.packages || []); }
    catch (err) { if (err.response?.status === 401) window.location.reload(); }
  }, [adminToken]);

  useEffect(() => { fetchPackages(); }, [fetchPackages]);

  const handleCreate = async () => {
    if (!newPkg.name || !newPkg.price) { toast.error("Name and price required"); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/admin/packages`, { name: newPkg.name, price: parseFloat(newPkg.price), payment_type: "one_time", features: newPkg.features.split("\n").filter(f => f.trim()), active: true }, { headers: getHeaders() });
      toast.success("Package created"); setNewPkg({ name: "", price: "", features: "" }); fetchPackages();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setCreating(false); }
  };

  const handleDelete = async (pkgId, permanent = false) => {
    const msg = permanent ? "PERMANENTLY delete this package? This cannot be undone." : "Deactivate this package?";
    if (!window.confirm(msg)) return;
    try { await axios.delete(`${API}/admin/packages/${pkgId}?permanent=${permanent}`, { headers: getHeaders() }); toast.success(permanent ? "Package permanently deleted" : "Package deactivated"); fetchPackages(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="grid md:grid-cols-3 gap-6">
      <Card className="bg-white border-[#E5E7EB]">
        <CardHeader><CardTitle className="text-lg text-[#111827]">Create Package</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div><Label className="text-sm text-slate-700">Name</Label><Input value={newPkg.name} onChange={(e) => setNewPkg({...newPkg, name: e.target.value})} placeholder="e.g. Growth Plan" className="bg-white border-slate-300" data-testid="pkg-name-input" /></div>
          <div><Label className="text-sm text-slate-700">Price ($)</Label><Input type="number" value={newPkg.price} onChange={(e) => setNewPkg({...newPkg, price: e.target.value})} placeholder="27.00" className="bg-white border-slate-300" data-testid="pkg-price-input" /></div>
          <div><Label className="text-sm text-slate-700">Features (one per line)</Label><textarea value={newPkg.features} onChange={(e) => setNewPkg({...newPkg, features: e.target.value})} placeholder={"Full recommendations\nPDF fix sheet"} rows={3} className="w-full rounded-md border border-slate-300 p-2 text-sm bg-white" /></div>
          <Button onClick={handleCreate} disabled={creating} className="w-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="create-pkg-btn">
            {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}Create Package
          </Button>
        </CardContent>
      </Card>
      <div className="md:col-span-2 space-y-4">
        <h3 className="font-semibold text-[#111827]">Existing Packages ({packages.length})</h3>
        {packages.map(pkg => (
          <Card key={pkg.id} className="bg-white border-[#E5E7EB]">
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-medium text-[#111827]">{pkg.name}</h4>
                    <Badge className={pkg.active ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-600"}>{pkg.active ? "Active" : "Inactive"}</Badge>
                  </div>
                  <p className="text-2xl font-bold text-[#111827]">${pkg.price} <span className="text-sm font-normal text-[#9CA3AF]">{pkg.currency?.toUpperCase() || "USD"}</span></p>
                  {pkg.features?.length > 0 && <div className="flex flex-wrap gap-1 mt-2">{pkg.features.slice(0, 4).map((f, i) => <Badge key={i} variant="secondary" className="text-xs bg-slate-100 text-slate-600">{f}</Badge>)}</div>}
                </div>
                <div className="flex items-center gap-1">
                  {pkg.active && <Button variant="ghost" size="sm" onClick={() => handleDelete(pkg.id, false)} className="text-amber-500" title="Deactivate"><Power className="w-4 h-4" /></Button>}
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(pkg.id, true)} className="text-red-500" title="Permanently delete" data-testid={`pkg-delete-${pkg.id}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
